import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';

// Atajo global (solo admin): 3 barras espaciadoras seguidas —fuera de un campo
// de texto— abren un popup con las Reglas de IA para alimentarla al vuelo.
// Escape (o clic afuera o "Listo") GUARDA y cierra: lo escrito se suma al
// lenguaje de la IA correspondiente al contexto actual.

const SEQ_WINDOW_MS = 600; // máximo entre espacio y espacio para contar la seña

const RULE_CONTEXTS = {
  equipment: {
    key: 'ai_rules',
    title: 'Reglas de IA',
    description: 'Lo que escribas acá se suma al lenguaje de la IA para el match de equipos.',
    placeholder: 'Ej: "pantalla led" siempre incluye módulos GLOSHINE + procesador NOVASTAR…',
  },
  rrhh: {
    key: 'rrhh_ai_rules',
    title: 'Reglas IA RRHH',
    description: 'Lo que escribas acá guía las sugerencias de personas entre Gestión y el plan semanal.',
    placeholder: 'Ej: HORARIOS: usar_horario_por_fase\nFASES_DE_GESTION: armado, evento, desarmado\nCONFLICTOS: marcar_conflicto',
  },
};

function currentRulesContext() {
  const path = window.location.pathname;
  if (path.startsWith('/rrhh/plan-semanal') || path.startsWith('/rrhh')) return RULE_CONTEXTS.rrhh;
  return RULE_CONTEXTS.equipment;
}

function isEditable(el) {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

export default function AiRulesQuickPopup() {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [context, setContext] = useState(RULE_CONTEXTS.equipment);
  const presses = useRef({ count: 0, last: 0 });
  const rulesRef = useRef('');
  const contextRef = useRef(RULE_CONTEXTS.equipment);
  const openRef = useRef(false);

  useEffect(() => { rulesRef.current = rules; }, [rules]);
  useEffect(() => { contextRef.current = context; }, [context]);
  useEffect(() => { openRef.current = open; }, [open]);

  useEffect(() => {
    function onKeyDown(e) {
      if (openRef.current) {
        if (e.key === 'Escape') { e.preventDefault(); saveAndClose(); }
        return;
      }
      if (e.key !== ' ' || isEditable(document.activeElement) || e.metaKey || e.ctrlKey || e.altKey) return;
      const now = Date.now();
      const p = presses.current;
      p.count = now - p.last <= SEQ_WINDOW_MS ? p.count + 1 : 1;
      p.last = now;
      if (p.count >= 2) e.preventDefault(); // que el 2º/3º espacio no scrollee la página
      if (p.count >= 3) {
        p.count = 0;
        openPopup();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line
  }, []);

  async function openPopup() {
    const nextContext = currentRulesContext();
    setContext(nextContext);
    contextRef.current = nextContext;
    setOpen(true);
    setLoading(true);
    try {
      const { data } = await api.get(`/settings/${nextContext.key}`);
      setRules(data?.value || '');
    } catch {
      setRules('');
    } finally {
      setLoading(false);
    }
  }

  async function saveAndClose() {
    setSaving(true);
    try {
      await api.put(`/settings/${contextRef.current.key}`, { value: rulesRef.current });
    } catch { /* si falla, las reglas quedan como estaban en el server */ }
    setSaving(false);
    setOpen(false);
  }

  if (!open) return null;

  return createPortal(
    <div className="modal-overlay" style={{ zIndex: 10002 }} onClick={saveAndClose}>
      <div className="modal" style={{ maxWidth: 560, width: '92vw' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
          <h3 className="modal-title" style={{ margin: 0 }}>{context.title}</h3>
          <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>Esc guarda y cierra</span>
        </div>
        <p style={{ color: 'var(--text-dim)', fontSize: 13, marginBottom: 10 }}>
          {context.description}
        </p>
        <textarea
          className="form-input"
          autoFocus
          rows={12}
          style={{ width: '100%', fontSize: 13, lineHeight: 1.45, resize: 'vertical' }}
          placeholder={loading ? 'Cargando…' : context.placeholder}
          value={rules}
          onChange={e => setRules(e.target.value)}
          disabled={loading}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button type="button" className="btn btn-primary" onClick={saveAndClose} disabled={saving || loading}>
            {saving ? 'Guardando…' : 'Listo'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
